import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getBannerAccent } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/config';
import { bannerFallbackIcon, webInteractive, withAlpha } from '@/components/Banner/bannerChrome';

export const ARROW_SIZE = 7;

export const getTooltipAccent = (contentType, colors) => {
  if (contentType === 'link' && colors) {
    // Tooltip surfaces stay dark even when the surrounding app uses a light theme.
    return '#FFFFFF';
  }
  return getBannerAccent(contentType, true, colors);
};

export const getTooltipCardBg = (colors) => '#1C1C1C';

const createStyles = (colors, isDark, accentColor, compact) => {
  const cardBg = getTooltipCardBg(colors);

  return StyleSheet.create({
    card: {
      backgroundColor: cardBg,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)',
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? {
        boxShadow: isDark
          ? '0 8px 28px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04)'
          : '0 10px 32px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08)',
      // Match the other Mac banners: native shadow updates can crash AppKit
      // during a theme change, so the card uses its surface and border there.
      } : Platform.OS === 'macos' ? {} : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: isDark ? 0.4 : 0.18,
        shadowRadius: 20,
        elevation: 8,
      }),
    },
    cardInner: {
      padding: compact ? 14 : 14,
      gap: compact ? 12 : 10,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 8,
    },
    contentCol: {
      flex: 1,
      gap: 8,
      minWidth: 0,
    },
    iconWell: {
      width: compact ? 28 : 30,
      height: compact ? 28 : 30,
      borderRadius: compact ? 14 : 15,
      backgroundColor: withAlpha(accentColor, 0.22),
      alignItems: 'center',
      justifyContent: 'center',
    },
    dismissBtn: {
      width: 26,
      height: 26,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      ...webInteractive,
    },
    dismissBtnHover: {
      backgroundColor: 'rgba(255,255,255,0.12)',
    },
    ctaBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: BORDER_RADIUS.small + 2,
      backgroundColor: withAlpha(accentColor, 0.22),
      borderWidth: 1,
      borderColor: withAlpha(accentColor, 0.32),
      ...webInteractive,
    },
    ctaBtnHover: {
      backgroundColor: withAlpha(accentColor, 0.32),
    },
  });
};

function TooltipCardBody({
  content,
  contentType,
  isDismissible,
  accentColor,
  colors,
  isDark,
  theme,
  onDismiss,
  onCTA,
  compact = false,
}) {
  const styles = useMemo(
    () => createStyles(colors, isDark, accentColor, compact),
    [colors, isDark, accentColor, compact],
  );

  const headlineSize = compact ? 's' : 'n';
  const bodySize = compact ? 'xs' : 's';
  const iconSize = compact ? 13 : 14;
  const iconName = content.icon || bannerFallbackIcon(contentType);

  return (
    <View style={styles.card}>
      <View style={styles.cardInner}>
        <View style={styles.topRow}>
          <View style={styles.contentCol}>
            <View style={styles.iconWell}>
              <Icon name={iconName} size={iconSize} color={accentColor} solid />
            </View>
            {!!content.headline && (
              <Text size={headlineSize} weight="bold" style={{ color: '#fff' }}>{content.headline}</Text>
            )}
            {!!content.subtitle && (
              <Text size={bodySize} style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 17 }}>{content.subtitle}</Text>
            )}
          </View>
          {isDismissible && (
            <Pressable
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              hitSlop={6}
              style={({ hovered, pressed }) => [
                styles.dismissBtn,
                Platform.OS === 'web' && hovered && styles.dismissBtnHover,
                pressed && { opacity: 0.7 },
              ]}
            >
              <Icon name="xmark" size={10} color="rgba(255,255,255,0.55)" />
            </Pressable>
          )}
        </View>
        {!!content.ctaText && (
          <Pressable
            onPress={onCTA}
            accessibilityRole="button"
            accessibilityLabel={content.ctaText}
            style={({ hovered, pressed }) => [
              styles.ctaBtn,
              Platform.OS === 'web' && hovered && styles.ctaBtnHover,
              pressed && { opacity: 0.88 },
            ]}
          >
            <Text size={bodySize} weight="bold" style={{ color: '#fff' }}>{content.ctaText}</Text>
            <Icon name="arrow-right" size={10} color="#fff" />
          </Pressable>
        )}
      </View>
    </View>
  );
}

export default React.memo(TooltipCardBody);
